import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IconPhoto } from '@tabler/icons-react';

import { getOptimizedImageUrl } from '../lib/imageUtils';
import { rewriteViaPlaceholderUrl } from '../lib/placeholderImage';

function resolveImageSrc(url) {
  if (!url) return null;
  if (typeof url === 'string' && url.includes('via.placeholder.com')) {
    return rewriteViaPlaceholderUrl(url);
  }
  return url;
}

/**
 * lazy + placeholder + 오류 시 CSS/아이콘 폴백 (외부 placeholder URL 미사용)
 */
function SafeImage({
  src,
  alt = '',
  className = '',
  width,
  height,
  loading = 'lazy',
  style,
  optimize = false,
  optimizeOptions,
  fallbackEmoji = null,
  onError: onErrorProp,
  onLoad: onLoadProp,
  ...imgRest
}) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const resolvedSrc = useMemo(() => {
    const normalized = resolveImageSrc(src);
    if (!normalized) return null;
    if (optimize) return getOptimizedImageUrl(normalized, optimizeOptions);
    return normalized;
  }, [optimize, src, optimizeOptions]);

  const showFallback = failed || !resolvedSrc;

  useEffect(() => {
    setFailed(false);
    setLoaded(false);
  }, [resolvedSrc]);

  const onError = useCallback(
    (e) => {
      setFailed(true);
      onErrorProp?.(e);
    },
    [onErrorProp],
  );

  const onLoad = useCallback(
    (e) => {
      setLoaded(true);
      onLoadProp?.(e);
    },
    [onLoadProp],
  );

  const fallbackNode =
    fallbackEmoji ?? <IconPhoto size={28} stroke={1.75} aria-hidden />;

  if (showFallback) {
    return (
      <span
        className={`mingo-safe-img mingo-safe-img--fallback ${className}`.trim()}
        style={style}
        role="img"
        aria-label={alt || undefined}
      >
        <span className="mingo-safe-img__fallback" aria-hidden="true">
          <span className="mingo-safe-img__fallback-icon">{fallbackNode}</span>
        </span>
      </span>
    );
  }

  return (
    <span className={`mingo-safe-img ${className}`.trim()} style={style}>
      {!loaded ? <span className="mingo-safe-img__ph" aria-hidden="true" /> : null}
      <img
        {...imgRest}
        src={resolvedSrc}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        decoding="async"
        className="mingo-safe-img__img"
        onError={onError}
        onLoad={onLoad}
        style={{ opacity: loaded ? 1 : 0 }}
      />
    </span>
  );
}

export default React.memo(SafeImage);
